import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";

const ACCESS_BADGE = {
  approved: ["bg-emerald-600 text-white", "Autorisée"],
  pending: ["bg-amber-100 text-amber-700", "En attente"],
  rejected: ["bg-red-100 text-red-700", "Refusée"],
  suspended: ["bg-red-100 text-red-700", "Suspendue"],
  none: ["bg-muted text-muted-foreground", "Non autorisée"],
};

/** Dialog admin : capacités opérationnelles d'un partenaire (contrôlé par Colis2Go). */
export const CapabilitiesDialog = ({ partner, open, onOpenChange }) => {
  const [gov, setGov] = useState(null);

  const load = () => api.get(`/admin/partners/${partner.id}/governance`).then(({ data }) => setGov(data));
  useEffect(() => { if (open && partner) load(); }, [open, partner?.id]); // eslint-disable-line

  const setCap = async (field, val) => {
    await api.put(`/admin/partners/${partner.id}/capabilities`, { [field]: val });
    toast.success("Capacité mise à jour");
    load();
  };
  const setHub = async (hubId, status) => {
    await api.put(`/admin/partners/${partner.id}/hub-access`, { hub_id: hubId, status });
    toast.success("Accès Hub mis à jour");
    load();
  };

  if (!partner) return null;
  const caps = gov?.capabilities || {};
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="capabilities-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-accent" /> Capacités opérationnelles — {partner.company_name}</DialogTitle>
          <DialogDescription>Le partenaire propose, Colis2Go autorise. Ces paramètres ne sont pas modifiables par le partenaire.</DialogDescription>
        </DialogHeader>
        {gov && (
          <div className="space-y-4">
            <div className="space-y-2">
              {[["direct_reception", "Réception directe (adresse communiquée au client)"],
                ["shopship_collection", "Collecte Shop & Ship"],
                ["air", "Mode aérien"],
                ["sea", "Mode maritime"]].map(([f, label]) => (
                <label key={f} className="flex items-center justify-between border border-border p-3 text-sm">
                  <span>{label}</span>
                  <Switch data-testid={`cap-${f}`} checked={!!caps[f]} onCheckedChange={(v) => setCap(f, v)} />
                </label>
              ))}
            </div>
            <div>
              <div className="font-heading font-bold text-sm">Collecte aux Hubs Colis2Go</div>
              <div className="mt-2 space-y-2">
                {gov.hubs.map((h) => {
                  const [cls, label] = ACCESS_BADGE[h.access_status] || ACCESS_BADGE.none;
                  return (
                    <div key={h.id} data-testid={`hubaccess-${h.id}`} className="flex flex-wrap items-center justify-between gap-2 border border-border p-3 text-sm">
                      <span className="font-medium">{h.name}</span>
                      <span className="flex items-center gap-2">
                        <Badge className={`rounded-sm ${cls}`}>{label}</Badge>
                        {h.access_status !== "approved" && <Button size="sm" data-testid={`grant-hub-${h.id}`} className="rounded-sm h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setHub(h.id, "approved")}>Autoriser</Button>}
                        {h.access_status === "approved" && <Button size="sm" variant="outline" data-testid={`suspend-hub-${h.id}`} className="rounded-sm h-7 text-xs text-red-600" onClick={() => setHub(h.id, "suspended")}>Suspendre</Button>}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

/** Liste admin des demandes d'accès aux Hubs (partenaire propose -> Colis2Go décide). */
export const HubAccessRequests = () => {
  const [reqs, setReqs] = useState([]);
  const load = () => api.get("/admin/hub-access").then(({ data }) => setReqs(data));
  useEffect(() => { load(); }, []);

  const decide = async (r, action) => {
    await api.post(`/admin/hub-access/${r.id}/decide`, { action });
    toast.success(action === "approve" ? "Accès approuvé" : action === "reject" ? "Demande refusée" : "Accès suspendu");
    load();
  };

  const pending = reqs.filter((r) => r.status === "pending");
  const others = reqs.filter((r) => r.status !== "pending");
  return (
    <div className="border border-border bg-card p-5">
      <h3 className="font-heading font-bold">Demandes d&rsquo;accès aux Hubs {pending.length > 0 && <Badge className="ml-1 rounded-sm bg-amber-100 text-amber-700">{pending.length} en attente</Badge>}</h3>
      <div className="mt-3 space-y-2">
        {pending.map((r) => (
          <div key={r.id} data-testid={`hubreq-${r.id}`} className="flex flex-wrap items-center justify-between gap-2 border border-amber-300 bg-amber-50 p-3 text-sm">
            <span><b>{r.partner_name}</b> demande l&rsquo;accès au <b>{r.hub_name}</b> <span className="text-muted-foreground">· {(r.requested_at || "").slice(0, 10)}</span></span>
            <span className="flex gap-1.5">
              <Button size="sm" data-testid={`hubreq-approve-${r.id}`} className="rounded-sm h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => decide(r, "approve")}>Approuver</Button>
              <Button size="sm" variant="outline" data-testid={`hubreq-reject-${r.id}`} className="rounded-sm h-7 text-xs text-red-600" onClick={() => decide(r, "reject")}>Refuser</Button>
            </span>
          </div>
        ))}
        {pending.length === 0 && <p className="text-xs text-muted-foreground">Aucune demande en attente.</p>}
        {others.slice(0, 10).map((r) => {
          const [cls, label] = ACCESS_BADGE[r.status] || ACCESS_BADGE.none;
          return (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 border border-border p-2.5 text-sm">
              <span>{r.partner_name} · {r.hub_name}</span>
              <span className="flex items-center gap-2">
                <Badge className={`rounded-sm ${cls}`}>{label}</Badge>
                {r.status === "approved" && <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs text-red-600" data-testid={`hubreq-suspend-${r.id}`} onClick={() => decide(r, "suspend")}>Suspendre</Button>}
                {r.status !== "approved" && <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" data-testid={`hubreq-reapprove-${r.id}`} onClick={() => decide(r, "approve")}>Autoriser</Button>}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** Carte partenaire : capacités en lecture seule + demandes d'accès aux Hubs. */
export const PartnerGovernanceCard = () => {
  const [gov, setGov] = useState(null);
  const load = () => api.get("/partner/governance").then(({ data }) => setGov(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const request = async (h) => {
    try {
      const { data } = await api.post("/partner/hub-access", { hub_id: h.id });
      toast.success(data.message || "Demande envoyée");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  if (!gov) return null;
  const caps = gov.capabilities || {};
  return (
    <div className="border border-border bg-card p-5" data-testid="partner-governance-card">
      <h3 className="font-heading font-bold flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-accent" /> Mes autorisations Colis2Go</h3>
      <p className="mt-1 text-xs text-muted-foreground">Ces paramètres sont contrôlés par Colis2Go. Vous pouvez faire une demande, l&rsquo;activation finale appartient à Colis2Go.</p>
      <div className="mt-3 flex flex-wrap gap-2 text-sm">
        <Badge data-testid="cap-direct-badge" className={`rounded-sm ${caps.direct_reception ? "bg-emerald-600 text-white" : "bg-muted text-muted-foreground"}`}>Réception directe : {caps.direct_reception ? "Autorisée" : "Non autorisée"}</Badge>
        <Badge className={`rounded-sm ${caps.shopship_collection ? "bg-emerald-600 text-white" : "bg-muted text-muted-foreground"}`}>Shop &amp; Ship : {caps.shopship_collection ? "Oui" : "Non"}</Badge>
        <Badge className={`rounded-sm ${caps.air ? "bg-emerald-600 text-white" : "bg-muted text-muted-foreground"}`}>Air : {caps.air ? "Oui" : "Non"}</Badge>
        <Badge className={`rounded-sm ${caps.sea ? "bg-emerald-600 text-white" : "bg-muted text-muted-foreground"}`}>Mer : {caps.sea ? "Oui" : "Non"}</Badge>
      </div>
      <div className="mt-4 space-y-2">
        {gov.hubs.map((h) => {
          const [cls, label] = ACCESS_BADGE[h.access_status] || ACCESS_BADGE.none;
          return (
            <div key={h.id} data-testid={`p-hubaccess-${h.id}`} className="flex flex-wrap items-center justify-between gap-2 border border-border p-3 text-sm">
              <span className="font-medium">{h.name}</span>
              <span className="flex items-center gap-2">
                <Badge className={`rounded-sm ${cls}`}>{h.access_status === "pending" ? "EN ATTENTE D'APPROBATION COLIS2GO" : label}</Badge>
                {["none", "rejected"].includes(h.access_status) && (
                  <Button size="sm" variant="outline" data-testid={`request-hub-${h.id}`} className="rounded-sm h-7 text-xs" onClick={() => request(h)}>Demander l&rsquo;accès</Button>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
